import { nameCase as nameCaseExternal } from '@foundernest/namecase';
import { titleCase as titleCaseExternal } from 'title-case';

import { US_STATES } from 'lib/constants/locales';

export function stringifyLatitude(latitude: number) {
  return `${Math.abs(latitude).toFixed(4)}° ${latitude > 0 ? 'N' : 'S'}`;
}

export function stringifyLongitude(longitude: number) {
  return `${Math.abs(longitude).toFixed(4)}° ${longitude > 0 ? 'E' : 'W'}`;
}

export function nameCase(name: string | undefined) {
  if (name == null) {
    return null;
  }

  return nameCaseExternal(name);
}

export function titleCase(name: string | undefined) {
  if (name == null) {
    return null;
  }

  return titleCaseExternal(name);
}

export function capitalizeSentence(sentence: string | undefined): string | undefined {
  if (typeof sentence === 'undefined' || !sentence) {
    return undefined;
  }

  const denylist = ['a', 'the', 'an', 'and', 'or', 'but', 'in', 'on', 'of', 'it'];

  return sentence
    .toLowerCase()
    .split(' ')
    .map((word) => (denylist.includes(word) ? word : titleCase(word)))
    .join(' ');
}

export function formatCompanyPosition(position: string | undefined) {
  if (position == null) {
    return null;
  }

  const lowered = position.toLowerCase();
  // Formats ceo, cto, cfo, etc as CEO, CTO, CFO, respectively
  // Also handes multiple words:
  //
  // 'interim ceo' => 'Interim CEO'
  lowered
    .split(' ')
    .map((word: string) => {
      if (word.length <= 4 && ['c'].includes(word[0])) {
        return word.toUpperCase();
      }
    })
    .join(' ');

  let titleName = titleCase(lowered);

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  titleName = titleName.replace(/\bVp/, ' VP');
  titleName = titleName.replace(/\bSvp/, ' SVP');
  titleName = titleName.replace(/\bCeo/, ' CEO');

  return titleName;
}

// NOTE: In cases where it's unclear whether or not an attribute is a
// person's name or a company name -- using this is sufficient.
//
export function formatCompanyName(text: string | undefined) {
  if (text == null) {
    return null;
  }

  let companyName = nameCase(text);

  // TODO: Make this work internationally
  // https://en.wikipedia.org/wiki/List_of_legal_entity_types_by_country

  // === European Union ===

  // Societas (SE)
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  companyName = companyName.replace(/\bSe/, ' SE');
  // Societas cooperativa (SCE)
  companyName = companyName.replace(/\bSce/, ' SCE');
  // Societas privata (SPE)
  companyName = companyName.replace(/\bSpe/, ' SPE');
  // Societas unius personae (SUP)
  companyName = companyName.replace(/\bSup/, ' SUP');
  // Economic interest grouping (EEIG)
  companyName = companyName.replace(/\bEeig/, ' EEIG');

  // === UK / Ireland / Commonwealth ===

  // Charitable incorporated organisation (CIO)
  companyName = companyName.replace(/\bCio/, ' CIO');
  // Community interest company (CIC)
  companyName = companyName.replace(/\bCic/, ' CIC');
  // Industrial and provident society (IPS)
  companyName = companyName.replace(/\bIps/, ' IPS');
  // Limited company (Ltd.)
  // Do nothing -- nameCase handles this fine
  // Public limited company (PLC) - note sometimes left as 'plc' but capitalizing should be safe
  companyName = companyName.replace(/\bPlc/, ' PLC');

  // === United States ===

  // Limited liability company (LLC)
  companyName = companyName.replace(/\bLlc/, ' LLC');

  // Limited liability limited partnership (LLLP)
  companyName = companyName.replace(/\bLllp/, ' LLLP');

  // === Germany / EU ===

  // Gesellschaft mit beschränkter Haftung (GmbH)
  companyName = companyName.replace(/\bGmbh/, ' GmbH');
  // Sometimes mbH if Gesselschaft is part of the company name
  companyName = companyName.replace(/\sMbh/, ' mbH');
  // gGmbH for non profits
  companyName = companyName.replace(/\bGgmbh/, ' gGmbH');

  // Map over US Names -- hack that we can do for now.
  // to make some organizations display better.
  companyName = companyName
    .split(' ')
    .map((word) => {
      if (Object.keys(US_STATES).includes(word.toUpperCase())) {
        return word.toUpperCase();
      }

      if (word.toUpperCase() === 'US') {
        return 'US';
      }

      if (word.toUpperCase() === 'USA') {
        return 'USA';
      }

      // HACK: Fix problems caused by namecasing
      if (word.toUpperCase() === 'SERVICES') {
        return 'Services';
      }

      if (word.toUpperCase() === 'SERVICE') {
        return 'Service';
      }

      // National Registered Agents Inc has an abbreviation
      if (word.toUpperCase() === 'NRAI') {
        return 'NRAI';
      }

      return word;
    })
    .join(' ');

  return companyName;
}
